/**
 * Zawężenie województwa do powiatów — czysta logika, wspólna dla karty firmy,
 * formularza postępowania i „Mojego zakresu".
 *
 * Stan trzymamy jak backend: wpis w mapie tylko dla województwa ZAWĘŻONEGO; brak
 * wpisu = całe województwo. Komplet zaznaczonych powiatów wraca do „brak wpisu", więc
 * nie ma dwóch różnych zapisów tego samego stanu, a powiat dodany kiedyś do słownika
 * sam wchodzi w zakres firmy z całym województwem.
 */

import type { VoivodeshipOption } from "@/features/rfq/api/scopes";

export type { CountyOption, VoivodeshipOption } from "@/features/rfq/api/scopes";

/** {kod województwa: [kody TERYT powiatów]} — tylko województwa zawężone. */
export type CountySelection = Record<string, string[]>;

/** Powiaty faktycznie objęte zasięgiem w województwie — komplet, gdy brak zawężenia. */
export function selectedCounties(
  option: VoivodeshipOption,
  selection: CountySelection,
): string[] {
  const narrowed = selection[option.value];
  return narrowed === undefined ? option.counties.map((c) => c.teryt) : narrowed;
}

/**
 * Ustaw listę powiatów województwa. Kolejność i zbiór bierze ze słownika (obce kody
 * odpadają); komplet kasuje wpis, bo komplet = brak zawężenia.
 */
export function withCounties(
  option: VoivodeshipOption,
  selection: CountySelection,
  counties: string[],
): CountySelection {
  const all = option.counties.map((c) => c.teryt);
  const kept = all.filter((t) => counties.includes(t));
  const rest = { ...selection };
  delete rest[option.value];
  return kept.length === all.length ? rest : { ...rest, [option.value]: kept };
}

export function toggleCounty(
  option: VoivodeshipOption,
  selection: CountySelection,
  teryt: string,
): CountySelection {
  const current = selectedCounties(option, selection);
  const next = current.includes(teryt)
    ? current.filter((t) => t !== teryt)
    : [...current, teryt];
  return withCounties(option, selection, next);
}

/** Zawężenie odjęte z województw, których już nie ma na liście. */
export function pruneCounties(selection: CountySelection, areas: string[]): CountySelection {
  return Object.fromEntries(
    Object.entries(selection).filter(([voivodeship]) => areas.includes(voivodeship)),
  );
}

/** Województwa zawężone do ZERA powiatów — stan, którego nie wolno zapisać. */
export function emptiedVoivodeships(selection: CountySelection): string[] {
  return Object.entries(selection)
    .filter(([, counties]) => counties.length === 0)
    .map(([voivodeship]) => voivodeship);
}

/** „wszystkie powiaty (20)" / „3 z 20 powiatów" / „żaden powiat". */
export function countySummary(selectedCount: number, total: number): string {
  if (total === 0) return "";
  if (selectedCount === total) return `wszystkie powiaty (${total})`;
  if (selectedCount === 0) return "żaden powiat";
  return `${selectedCount} z ${total} powiatów`;
}
